import React, {useEffect, useRef, useState} from 'react';
import {makeStyles} from "@mui/styles";
import Paper from '@mui/material/Paper';
import {CircularProgress, TextField as MuiTextField} from "@mui/material";
import IconButton from "../button/icon_button";
import {Search} from '@mui/icons-material';
import classnames from "classnames";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
    root: {
        width: props => props.fullWidth ? '100%' : props.width ? props.width : 100,
        padding: '2px 20px',
        display: 'flex',
        alignItems: 'center',
        color: theme.vars.palette.background.paper,
        boxShadow: 'none',
        '& .MuiInputBase-root ': {
            marginBottom: props => props.variant !== 'outlined' ? '16px' : 'unset',
        },
    },
    input: {
        flex: 1,
        color: theme.vars.palette.background.paper,
        [`& input`]: {
            backgroundColor: 'transparent',
        },
    },
    icon: {
        padding: '0.145em',
        width: '1.2em',
        height: '1.2em',
    },
}));

const SearchInput = ({
                         loading = false,
                         placeholder = 'type...',
                         label,
                         ariaLabel = 'input',
                         initialValue = '',
                         type = 'text',
                         search,
                         setEnteredKeyword,
                         variant = 'standard',
                         autoSearch = false,
                         iconButtonProps,
                         fullWidth = true,
                         width,
                         timeout = 300,
                         disabled,
                         paperProps,
                         immediateSearch = false,
                         ...props
                     }) => {
    const classes = useStyles({fullWidth, width, variant});
    const [res, setRes] = useState(initialValue);
    const [searched, setSearched] = useState(initialValue);
    const cancelTokenRef = useRef(null);

    /**
     * Listens to the changes of initial Values and with each change:
     * sets the result of the search input.
     */
    useEffect(() => {
        setRes(initialValue);
        setSearched(initialValue)
    }, [initialValue])

    /**
     * Listen for the following values and with each of their changes:
     * ** autoSearch, res, searched, loading
     * sets a timer that by the end of it, will invoke the search callback.
     */
    useEffect(() => {
        if (!autoSearch || (loading && !immediateSearch) || res === searched)
            return;
        if (immediateSearch && search) {
            setSearched(res);
            cancelTokenRef.current?.cancel();
            cancelTokenRef.current = axios.CancelToken.source();
            search(res, cancelTokenRef.current);
        } else {
            const timer = setTimeout(() => {
                if (!autoSearch || (loading && !immediateSearch) || res === searched) return;
                if (search) {
                    setSearched(res);
                    cancelTokenRef.current?.cancel();
                    cancelTokenRef.current = axios.CancelToken.source();
                    search(res, cancelTokenRef.current);
                }
            }, timeout);
            return () => clearTimeout(timer);
        }
    }, [autoSearch, res, searched, loading, immediateSearch, timeout])


    /**
     * Searches using the value of the input as soon as the search icon or enter is pressed.
     * @param {MouseEvent} e
     */
    const onSearchPressed = (e) => {
        cancelTokenRef.current?.cancel();
        cancelTokenRef.current = axios.CancelToken.source();
        if (search) {
            if (autoSearch) {
                search(searched, cancelTokenRef.current);
            } else {
                search(res)
            }
        }
    }

    /**
     * Listens for the keys that are to be released in the document.
     *
     * * If the released key is an enter key,then searches using the current value of input.
     *
     * @param {KeyboardEvent} e
     */
    const onKeyUp = (e) => {
        switch (e.key) {
            case 'Enter':
                onSearchPressed(e);
                break;
            default:
                break;
        }
    }

    return (
        <Paper {...paperProps} className={classnames(classes.root, paperProps?.className, 'search-input')}>
            <MuiTextField
                fullWidth={fullWidth}
                type={type}
                variant={variant}
                label={label}
                name={label}
                value={res ?? ""}
                onChange={(e) => {
                    setRes(e.target.value)
                    if (setEnteredKeyword)
                        setEnteredKeyword(e.target.value);
                }}
                className={classes.input}
                placeholder={placeholder}
                onKeyUp={onKeyUp}
                InputProps={{
                    ...(props.InputProps ?? {}),
                    ...(
                        variant === 'standard'
                            ? {disableUnderline: true}
                            : {}
                    ),
                    endAdornment: (
                        <IconButton
                            disabled={loading || disabled}
                            onPressed={onSearchPressed}
                            color={'secondary'}
                            {...iconButtonProps}
                        >
                            {
                                loading
                                    ? <CircularProgress size={25} className={classes.icon}/>
                                    : <Search className={classes.icon}/>
                            }
                        </IconButton>
                    )
                }}
                inputProps={{'aria-label': ariaLabel}}
                {...props}
            />
        </Paper>
    );
}

export default SearchInput;
